/**
 * Filing Compliance Dashboard
 * Shows per-fee-earner filing stats for managers.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, FileCheck, Users, TrendingUp, Activity, Send } from "lucide-react";

import { API } from '../../config/api';

export default function FilingCompliance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");

  const [sending, setSending] = useState(false);

  const userId = localStorage.getItem("bonsai_user_id");

  useEffect(() => {
    if (!userId) { window.location.href = "/login"; }
  }, [userId]);

  async function fetchData() {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/bonsai/filing/compliance/dashboard?days=${days}`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      const d = await resp.json();
      setData(d);
    } catch (e) {
      console.error("Failed to fetch compliance data:", e);
    }
    setLoading(false);
  }

  useEffect(() => { fetchData(); }, [days]);

  const summary = data?.summary || {};
  const team = data?.team_stats || [];
  const maxFiled = Math.max(...team.map(t => t.filed_count), 1);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="filing-compliance-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold" data-testid="compliance-title">Filing Compliance</h1>
              <p className="text-sm text-gray-400">Team filing activity and discipline</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              setSending(true);
              try {
                await fetch(`${API}/bonsai/emails/reports/filing-compliance-email?send_email=true&days=${days}`, {
                  method: "POST",
                  headers: { "X-Bonsai-User-Id": userId },
                });
                alert("Filing compliance report emailed!");
              } catch (e) { alert("Failed: " + e.message); }
              setSending(false);
            }} disabled={sending} className="border-gray-700 text-gray-300 h-8" data-testid="email-report-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending..." : "Email report"}
            </Button>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32 bg-gray-900 border-gray-700 text-sm h-8" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <FileCheck className="w-5 h-5 text-status-green mx-auto mb-1" />
              <div className="text-2xl font-bold text-white" data-testid="total-filed">{summary.total_filed || 0}</div>
              <div className="text-xs text-gray-400">Emails filed</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-white" data-testid="active-filers">{summary.active_filers || 0}</div>
              <div className="text-xs text-gray-400">Active filers</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <Activity className="w-5 h-5 text-purple-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-white" data-testid="total-fee-earners">{summary.total_fee_earners || 0}</div>
              <div className="text-xs text-gray-400">Fee earners</div>
            </CardContent>
          </Card>
        </div>

        {/* Fee Earner Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-gray-300">Fee Earner Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading...</div>
            ) : team.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No filing data available.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="compliance-table">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-400 text-xs">
                      <th className="px-4 py-2 text-left font-medium">Fee Earner</th>
                      <th className="px-4 py-2 text-left font-medium">Department</th>
                      <th className="px-4 py-2 text-right font-medium">Filed</th>
                      <th className="px-4 py-2 text-right font-medium">Matters</th>
                      <th className="px-4 py-2 text-left font-medium">Activity</th>
                      <th className="px-4 py-2 text-left font-medium">Last filed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map((fe) => (
                      <tr key={fe.email} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid="compliance-row">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-300">{fe.initials}</div>
                            <span className="text-xs font-medium text-white">{fe.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{fe.department}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`text-xs font-bold ${fe.filed_count > 0 ? "text-status-green" : "text-gray-500"}`}>{fe.filed_count}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs text-gray-300">{fe.unique_matters}</td>
                        <td className="px-4 py-2.5">
                          <div className="w-24 h-2 bg-gray-800 rounded-md overflow-hidden">
                            <div
                              className="h-full rounded-md bg-gradient-to-r from-green-500 to-teal transition-all"
                              style={{ width: `${Math.round((fe.filed_count / maxFiled) * 100)}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">
                          {fe.last_filed_at ? new Date(fe.last_filed_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="text-xs text-gray-500 mt-2 text-right">Last {days} days</div>
      </div>
    </div>
  );
}
